// One rule pack's result for one person (TGCLOUD-1910): the outcome, the rules
// that fired in their published wording with the relatives that satisfy them,
// the closest near-misses, what could not be evaluated, what the record held
// that could not be counted, and the attribution and disclaimer the pack's
// licence requires. Used for a live evaluation (TriagePanel) and for a stored
// one (RiskRecordsList's detail view), which keeps the pack version it was
// produced by.
import React from 'react';
import { Alert, Box, Button, Chip, Divider, Typography } from '@mui/material';
import { useAutoTranslation, useLanguage } from 'src/hooks/useTranslate';
import useDateFormatter from 'src/hooks/useDateFormatter';
import { toLocalDateFromInstant } from 'src/util/partialDate';
import { CLINICAL_COLOURS } from 'src/theme/palette';
import { usePedigreeInstance } from '../../../pedigree-tool/pedigree/instanceContext';
import { highlightCanvasNodes } from '../clinicalInsightsUi';
import RuleWording from './RuleWording';
import {
  dataNoteLines,
  eventsText,
  failureText,
  needText,
  outcomeText,
  refusalText,
  ruleOutcomeText
} from './triageText';
import { namerFor, useTriagePeople } from './triagePeople';

export const NEAR_MISSES_SHOWN = 3;

/**
 * A stored result (riskfactors.triageResult) in the shape of a live one, so
 * one component draws both.
 */
export const fromStored = (r) =>
  r
    ? {
        packId: r.packId,
        version: r.packVersion,
        name: r.packName,
        shortName: r.shortName || r.packName,
        publisher: r.publisher,
        publicationDate: r.publicationDate,
        lastUpdated: r.lastUpdated,
        sourceUrl: r.sourceUrl,
        licence: {
          name: r.licenceName,
          attribution: r.attribution,
          disclaimer: r.disclaimer,
          // A stored result kept them because the licence asked for them.
          displayRequired: Boolean(r.attribution || r.disclaimer)
        },
        status: 'evaluated',
        outcome: r.outcome,
        conditional: r.conditional,
        fired: r.fired || [],
        nearMisses: r.nearMisses || [],
        notEvaluated: r.notEvaluated || [],
        notApplicable: r.notApplicable || [],
        dataNotes: r.dataNotes || [],
        people: r.people || {},
        rulesEvaluated: r.rulesEvaluated,
        engineVersion: r.engineVersion,
        conceptSetsVersion: r.conceptSetsVersion,
        territory: r.territory,
        graphSource: r.graphSource,
        evaluatedAt: r.evaluatedAt
      }
    : null;

const pidsOf = (keys, people) =>
  (keys || [])
    .map((k) => people?.[k]?.pid)
    .filter((p) => p !== null && p !== undefined)
    .map(String);

function OutcomeChip({ outcome }) {
  const t = useAutoTranslation();
  const meets = outcome && outcome !== 'DOES_NOT_MEET';
  return (
    <Chip
      size="small"
      label={outcomeText(outcome, t)}
      variant={meets ? 'filled' : 'outlined'}
      sx={
        meets
          ? {
              bgcolor: CLINICAL_COLOURS.riskScore,
              color: '#fff',
              fontWeight: 700,
              height: 'auto',
              '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 }
            }
          : {
              fontWeight: 700,
              height: 'auto',
              '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 }
            }
      }
    />
  );
}

// A relative who satisfies a rule: named from the pedigree, linked to the chart.
function PersonChip({ refKey, people, nameOf }) {
  const t = useAutoTranslation();
  const { onSelectMember } = useTriagePeople();
  const { containerRef } = usePedigreeInstance();
  const ref = people?.[refKey];
  if (!ref) return null;
  const pid = ref.pid;
  const linked =
    typeof onSelectMember === 'function' && pid !== null && pid !== undefined;
  const detail = eventsText(ref.events, ref.bilateral, t);
  const label = detail
    ? t('{{person}}: {{diagnoses}}', {
        person: nameOf(refKey),
        diagnoses: detail
      })
    : nameOf(refKey);
  return (
    <Chip
      size="small"
      variant="outlined"
      label={label}
      data-testid="triage-person"
      onClick={linked ? () => onSelectMember(pid) : undefined}
      onMouseEnter={
        pid !== null && pid !== undefined
          ? () =>
              highlightCanvasNodes([String(pid)], true, containerRef?.current)
          : undefined
      }
      onMouseLeave={() =>
        highlightCanvasNodes([], false, containerRef?.current)
      }
      sx={{
        height: 'auto',
        maxWidth: '100%',
        mr: 0.5,
        mb: 0.5,
        '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 }
      }}
    />
  );
}

function SectionLabel({ children }) {
  return (
    <Typography
      sx={{
        fontSize: '.7rem',
        fontWeight: 700,
        letterSpacing: '.06em',
        textTransform: 'uppercase',
        color: 'text.secondary',
        mt: 1.5,
        mb: 0.5
      }}
    >
      {children}
    </Typography>
  );
}

function FiredRule({ rule, result, nameOf }) {
  const t = useAutoTranslation();
  const { containerRef } = usePedigreeInstance();
  const pids = pidsOf(rule.people, result.people);
  const outcome = ruleOutcomeText(rule.outcome, t);
  return (
    <Box
      data-testid="triage-fired"
      onMouseEnter={() =>
        pids.length && highlightCanvasNodes(pids, true, containerRef?.current)
      }
      onMouseLeave={() =>
        highlightCanvasNodes([], false, containerRef?.current)
      }
      sx={{
        border: '1px solid rgba(46,55,71,0.14)',
        borderRadius: 1,
        px: 1.25,
        py: 1,
        mb: 1
      }}
    >
      <RuleWording
        rule={rule}
        packId={result.packId}
        packVersion={result.version}
      />
      {outcome && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
          {t('Outcome: {{outcome}}', { outcome })}
        </Typography>
      )}
      {Array.isArray(rule.confirmations) && rule.confirmations.length > 0 && (
        <Box sx={{ mt: 0.75 }}>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {t('Met on the record, subject to:')}
          </Typography>
          {rule.confirmations.map((c, i) => (
            <Typography
              key={i}
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}
            >
              {c}
            </Typography>
          ))}
        </Box>
      )}
      {Array.isArray(rule.people) && rule.people.length > 0 && (
        <Box sx={{ mt: 0.75 }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.25 }}>
            {t('Who meets it')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {rule.people.map((k) => (
              <PersonChip
                key={k}
                refKey={k}
                people={result.people}
                nameOf={nameOf}
              />
            ))}
          </Box>
        </Box>
      )}
      {rule.interpretation && (
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 0.75, color: 'text.secondary' }}
        >
          {t('How TrakGene reads it:')} {rule.interpretation}
        </Typography>
      )}
    </Box>
  );
}

function NearMiss({ rule, nameOf }) {
  const t = useAutoTranslation();
  const needs = (rule.needs || []).map((n) => needText(n, { t, nameOf }));
  return (
    <Box data-testid="triage-near-miss" sx={{ mb: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {t('Did not meet {{section}}, failed only on: {{failure}}', {
          section: rule.section || rule.ruleId,
          failure: failureText(rule.failedOn, { t, nameOf })
        })}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}
      >
        {rule.text}
      </Typography>
      {needs.length > 0 && (
        <Typography variant="caption" sx={{ display: 'block' }}>
          {t('Also not recorded: {{needs}}', { needs: needs.join('; ') })}
        </Typography>
      )}
    </Box>
  );
}

function NotEvaluated({ rule, nameOf }) {
  const t = useAutoTranslation();
  const needs = (rule.needs || []).map((n) => needText(n, { t, nameOf }));
  return (
    <Box data-testid="triage-not-evaluated" sx={{ mb: 1 }}>
      <Typography
        variant="body2"
        sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}
      >
        {rule.section ? `${rule.section}: ` : ''}
        {rule.text}
      </Typography>
      {needs.length > 0 && (
        <Typography variant="caption" sx={{ display: 'block' }}>
          {rule.partlyMet
            ? t('Partly met. Needs: {{needs}}', { needs: needs.join('; ') })
            : t('Needs: {{needs}}', { needs: needs.join('; ') })}
        </Typography>
      )}
    </Box>
  );
}

/**
 * @param {object} props
 * @param {object} props.result   a pack result (live, or fromStored(...))
 * @param {boolean} [props.stored] a stored result: says so, and when
 */
export default function PackResult({ result, stored = false }) {
  const t = useAutoTranslation();
  const lang = useLanguage();
  const fmtDate = useDateFormatter();
  const { memberRecords, anonymous } = useTriagePeople();
  const [allNear, setAllNear] = React.useState(false);
  const [showNotEvaluated, setShowNotEvaluated] = React.useState(false);

  const nameOf = React.useMemo(
    () => namerFor(result?.people, { memberRecords, anonymous, t }),
    [result?.people, memberRecords, anonymous, t]
  );

  if (!result) return null;
  const packName = result.shortName || result.name || result.packId;
  const published = result.publicationDate
    ? fmtDate(result.publicationDate)
    : '';
  const evaluated = result.evaluatedAt
    ? fmtDate(toLocalDateFromInstant(result.evaluatedAt))
    : '';

  const header = (
    <Box sx={{ mb: 1 }}>
      <Typography sx={{ fontWeight: 700 }}>{packName}</Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {published
          ? t('Version {{version}}, {{publisher}}, published {{date}}', {
              version: result.version,
              publisher: result.publisher || '',
              date: published
            })
          : t('Version {{version}}, {{publisher}}', {
              version: result.version,
              publisher: result.publisher || ''
            })}
      </Typography>
    </Box>
  );

  const licence = result.licence || {};
  const showLicence =
    licence.displayRequired && (licence.attribution || licence.disclaimer);
  const footer = (
    <Box sx={{ mt: 1.5 }}>
      {result.routingNote && (
        <Typography
          variant="caption"
          sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}
        >
          {result.routingNote}
        </Typography>
      )}
      {showLicence && (
        <Box data-testid="triage-licence">
          {licence.attribution && (
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'text.secondary' }}
            >
              {licence.attribution}
            </Typography>
          )}
          {licence.disclaimer && (
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}
            >
              {licence.disclaimer}
            </Typography>
          )}
        </Box>
      )}
      {evaluated && (
        <Typography
          variant="caption"
          sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}
        >
          {stored
            ? t('Recorded result, evaluated {{date}}, engine {{engine}}', {
                date: evaluated,
                engine: result.engineVersion || ''
              })
            : t('Evaluated {{date}}, engine {{engine}}', {
                date: evaluated,
                engine: result.engineVersion || ''
              })}
        </Typography>
      )}
    </Box>
  );

  if (result.status === 'refused') {
    return (
      <Box data-testid="triage-pack" data-pack={result.packId}>
        {header}
        <Alert severity="info" data-testid="triage-refused">
          {refusalText(result.refusal, { t, pack: packName, lang })}
        </Alert>
        {footer}
      </Box>
    );
  }

  const fired = result.fired || [];
  const near = result.nearMisses || [];
  const notEvaluated = result.notEvaluated || [];
  const notes = (result.dataNotes || []).flatMap((n) =>
    dataNoteLines(n, { t, nameOf })
  );
  const shownNear = allNear ? near : near.slice(0, NEAR_MISSES_SHOWN);

  return (
    <Box data-testid="triage-pack" data-pack={result.packId}>
      {header}
      <Box sx={{ mb: 1 }} data-testid="triage-outcome">
        <OutcomeChip outcome={result.outcome} />
        {result.conditional && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
            {t(
              'Met on what the record holds; each rule names what still has to be confirmed.'
            )}
          </Typography>
        )}
      </Box>

      {fired.length > 0 ? (
        <>
          <SectionLabel>
            {fired.length === 1
              ? t('1 criterion met')
              : t('{{count}} criteria met', { count: fired.length })}
          </SectionLabel>
          {fired.map((rule) => (
            <FiredRule
              key={rule.ruleId}
              rule={rule}
              result={result}
              nameOf={nameOf}
            />
          ))}
        </>
      ) : (
        <Typography variant="body2" data-testid="triage-none-met">
          {result.rulesEvaluated === 1
            ? t('The criterion is not met.')
            : t('None of the {{count}} criteria is met.', {
                count: result.rulesEvaluated ?? 0
              })}
        </Typography>
      )}

      {near.length > 0 && (
        <>
          <SectionLabel>{t('Closest criteria not met')}</SectionLabel>
          {shownNear.map((rule) => (
            <NearMiss key={rule.ruleId} rule={rule} nameOf={nameOf} />
          ))}
          {near.length > NEAR_MISSES_SHOWN && (
            <Button
              size="small"
              onClick={() => setAllNear((v) => !v)}
              sx={{ px: 0, minWidth: 0, textTransform: 'none' }}
            >
              {allNear
                ? t('Show fewer')
                : t('Show all {{count}}', { count: near.length })}
            </Button>
          )}
        </>
      )}

      {notEvaluated.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Button
            size="small"
            onClick={() => setShowNotEvaluated((v) => !v)}
            sx={{ px: 0, minWidth: 0, textTransform: 'none' }}
            data-testid="triage-not-evaluated-toggle"
          >
            {notEvaluated.length === 1
              ? t('1 criterion could not be evaluated')
              : t('{{count}} criteria could not be evaluated', {
                  count: notEvaluated.length
                })}
          </Button>
          {showNotEvaluated &&
            notEvaluated.map((rule) => (
              <NotEvaluated key={rule.ruleId} rule={rule} nameOf={nameOf} />
            ))}
        </>
      )}

      {notes.length > 0 && (
        <Box sx={{ mt: 1 }} data-testid="triage-data-notes">
          <SectionLabel>
            {t('What the record holds that was not counted')}
          </SectionLabel>
          {notes.map((line, i) => (
            <Typography key={i} variant="body2" sx={{ mb: 0.25 }}>
              {line}
            </Typography>
          ))}
        </Box>
      )}

      {footer}
    </Box>
  );
}
